import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  XmarkIcon,
} from '@revfluence/fresh-icons/solid/esm';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import { CreatorProfileCard } from './components/creator-profile-card';
import { useCreatorProfileCardProps } from './components/creator-profile-card/hooks';
import { useRecentPostsProps } from './components/recent-posts-section/hooks';
import { RecentPostsSection } from './components/recent-posts-section';
import { useMajorAudienceDemographicsProps } from './components/major-audience-demographics-section/hooks';
import { MajorAudienceDemographicsSection } from './components/major-audience-demographics-section';
import { useSocialStatsProps } from './components/social-stats-section/hooks';
import { SocialStatsSection } from './components/social-stats-section';
import { CreatorAvatarHeader } from './components/creator-avatar-header/creator-avatar-header';
import { useCreatorAvatarHeaderProps } from './components/creator-avatar-header/hooks';

interface InstagramCreatorOverviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creator: InstagramCreator | null;
  isLoading: boolean;
}

export const InstagramCreatorOverview: React.FC<InstagramCreatorOverviewProps> = ({
  open,
  onOpenChange,
  creator,
  isLoading,
}) => {
  const creatorAvatarHeaderProps = useCreatorAvatarHeaderProps(creator, isLoading);
  const profileCardProps = useCreatorProfileCardProps(creator, isLoading);
  const recentPostsProps = useRecentPostsProps(creator, isLoading);
  const majorAudienceDemographicsProps = useMajorAudienceDemographicsProps(creator, isLoading);
  const socialStatsProps = useSocialStatsProps(isLoading, creator);

  const sheetTitleText = 'Creator Overview';
  const sheetDescriptionText = creator ? `Detailed overview for Instagram creator @${creator.username}.` : 'Detailed overview of an Instagram creator.';
  const sheetDescriptionLoadingText = 'Loading creator overview details.';

  if (isLoading) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-lg md:max-w-xl lg:max-w-2xl z-[200]" side="right">
          <SheetHeader className="px-4 py-3 border-b sticky top-0 bg-background z-10">
            <div className="flex justify-between items-center">
              <div>
                <SheetTitle className="text-lg font-semibold">{sheetTitleText}</SheetTitle>
                <SheetDescription className="sr-only">{sheetDescriptionLoadingText}</SheetDescription>
              </div>
              <SheetClose asChild>
                <Button variant="ghost" size="icon" aria-label="Close overview" disabled>
                  <XmarkIcon className="h-5 w-5" />
                </Button>
              </SheetClose>
            </div>
          </SheetHeader>
          <div className="space-y-6 flex-grow bg-gray-100">
            <CreatorAvatarHeader {...creatorAvatarHeaderProps} />

            <CreatorProfileCard {...profileCardProps} />

            <RecentPostsSection {...recentPostsProps} />

            <MajorAudienceDemographicsSection {...majorAudienceDemographicsProps} />

            <SocialStatsSection {...socialStatsProps} />

            <div className="h-10" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!creator) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-lg md:max-w-xl lg:max-w-2xl z-[200]" side="right">
        <div className="flex flex-col h-full">
          <SheetHeader className="px-4 py-3 border-b sticky top-0 bg-background z-10">
            <div className="flex justify-between items-center">
              <div>
                <SheetTitle className="text-lg font-semibold">{sheetTitleText}</SheetTitle>
                <SheetDescription className="sr-only">{sheetDescriptionText}</SheetDescription>
              </div>
              <SheetClose asChild>
                <Button variant="ghost" size="icon" aria-label="Close overview">
                  <XmarkIcon className="h-5 w-5" />
                </Button>
              </SheetClose>
            </div>
          </SheetHeader>

          <div className="space-y-2 flex-grow pb-5 bg-white">
            <CreatorAvatarHeader {...creatorAvatarHeaderProps} />

            <CreatorProfileCard {...profileCardProps} />

            <RecentPostsSection {...recentPostsProps} />

            <MajorAudienceDemographicsSection {...majorAudienceDemographicsProps} />

            <SocialStatsSection {...socialStatsProps} />

            <div className="h-10" />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InstagramCreatorOverview;
